import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ragAPI } from '../../services/api';
import { normaliseUrl } from '../../utils/normaliseUrl';
import PasteContentRecovery from '../../components/rag/PasteContentRecovery';
import { trackOnboarding } from '../../services/onboardingTelemetry';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import {
  GlobeAltIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  DocumentTextIcon,
  LinkIcon,
  FolderIcon,
  CogIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const WebScraper = ({ initialCollectionId = '', embedded = false, onComplete, initialUrl = '', resolveCollection }) => {
  const user = useAuthStore((state) => state.user);
  const canManageSources = Boolean(
    user?.is_superuser ||
    user?.role === 'superadmin' ||
    user?.tenant_role === 'owner' ||
    user?.tenant_role === 'admin' ||
    (user?.role === 'admin' && user?.tenant_id)
  );
  const [url, setUrl] = useState('');
  const [collectionId, setCollectionId] = useState('');
  const [collections, setCollections] = useState([]);
  const [collectionSearch, setCollectionSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [options, setOptions] = useState({
    max_pages: 50,
    max_depth: 3,
    include_patterns: '',
    exclude_patterns: '',
    use_sitemap: true,
    rate_limit_delay: 0.5,
  });
  const [configDefaults, setConfigDefaults] = useState(null);
  
  // Background job tracking
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const pollingRef = useRef(null);
  const attachedJobRef = useRef(null);
  const siteRefreshInFlightRef = useRef(false);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // "View extracted text" — lets an operator inspect exactly what a page captured,
  // so a thin/silent-failure page is visible and fixable rather than hidden.
  const [viewingPage, setViewingPage] = useState(null); // {url, title, content, loading}

  const viewPageText = async (page) => {
    const jobId = jobStatus?.id || currentJobId;
    if (!jobId || !page?.id) return;
    setViewingPage({ url: page.url, title: page.title, content: '', loading: true });
    try {
      const resp = await ragAPI.getScrapePageContent(jobId, page.id);
      const data = resp.data || resp;
      setViewingPage({ url: data.url, title: data.title, content: data.content || '', loading: false });
    } catch (e) {
      setViewingPage({ url: page.url, title: page.title, content: '', loading: false, error: 'Could not load extracted text.' });
    }
  };
  
  // Job History
  const [pastJobs, setPastJobs] = useState([]);
  const [showHistory, setShowHistory] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedJobPages, setSelectedJobPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [retryingUrl, setRetryingUrl] = useState(null);
  const [refreshingSiteJobId, setRefreshingSiteJobId] = useState(null);
  
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const attachedJobId = searchParams.get('job');

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  useEffect(() => {
    loadCollections();
    loadScraperConfig();
    if (!embedded) {
      loadJobHistory();
    }
  }, []);

  const loadScraperConfig = async () => {
    try {
      const response = await ragAPI.getScraperConfig();
      if (response.data?.defaults) {
        const defaults = response.data.defaults;
        setConfigDefaults(defaults);
        setOptions(prev => ({
          ...prev,
          max_pages: defaults.max_pages || prev.max_pages,
          max_depth: defaults.max_depth || prev.max_depth,
          use_sitemap: defaults.use_sitemap ?? prev.use_sitemap,
          rate_limit_delay: defaults.rate_limit_delay || prev.rate_limit_delay,
        }));
      }
    } catch (err) {
      console.log('Could not load scraper config, using defaults');
    }
  };

  const loadCollections = async () => {
    setLoading(true);
    try {
      const response = await ragAPI.getCollections();
      const available = response.data?.collections || [];
      setCollections(available);
      if (initialCollectionId && available.some((item) => String(item.id) === String(initialCollectionId))) {
        setCollectionId(String(initialCollectionId));
      }
    } catch (err) {
      console.error('Failed to load collections', err);
      setError('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const loadJobHistory = async () => {
    try {
      const response = await ragAPI.listScrapeJobs(null, 30);
      setPastJobs(response.data?.jobs || []);
    } catch (err) {
      console.error('Failed to load job history', err);
    }
  };

  const loadJobPages = async (jobId) => {
    if (selectedJob === jobId) {
      setSelectedJob(null);
      setSelectedJobPages([]);
      return;
    }
    setSelectedJob(jobId);
    setLoadingPages(true);
    try {
      const response = await ragAPI.getJobPages(jobId);
      setSelectedJobPages(response.data?.pages || []);
    } catch (err) {
      console.error('Failed to load job pages', err);
      setSelectedJobPages([]);
    } finally {
      setLoadingPages(false);
    }
  };

  const handlePageRetry = async (jobId, pageUrl, rescrape) => {
    setRetryingUrl(pageUrl);
    try {
      await ragAPI.retryUrl(jobId, pageUrl, rescrape);
      // Refresh pages after a short delay
      setTimeout(async () => {
        try {
          const response = await ragAPI.getJobPages(jobId);
          setSelectedJobPages(response.data?.pages || []);
        } catch (e) {}
        setRetryingUrl(null);
      }, 2000);
      // Also start polling if not already
      if (!pollingRef.current) {
        setCurrentJobId(jobId);
        startPolling(jobId);
      }
    } catch (err) {
      console.error('Page retry failed:', err);
      setRetryingUrl(null);
    }
  };

  const handleResumeJob = async (jobId) => {
    setCurrentJobId(jobId);
    setError('');
    setResult(null);
    setScraping(true);
    try {
      await ragAPI.resumeScrapeJob(jobId);
      startPolling(jobId);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Resume failed';
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      setScraping(false);
    }
  };

  const validateUrl = (urlString) => {
    try {
      new URL(urlString);
      return true;
    } catch {
      return false;
    }
  };

  // Poll for job status updates
  const startPolling = (jobId) => {
    // Clear any existing polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    
    let consecutiveFailures = 0;
    const stopPolling = () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };

    const pollJobStatus = async () => {
      try {
        const response = await ragAPI.getScrapeJobStatus(jobId);
        const status = response.data;
        consecutiveFailures = 0;
        setJobStatus(status);
        
        console.log('📊 Job status:', status.status, status.progress);
        
        // Check if job is complete
        if (status.status === 'completed' || status.status === 'completed_with_errors') {
          stopPolling();
          setScraping(false);
          // Convert job status to result format
          // A job can finish 'completed' having indexed nothing: a site that
          // 403s every request still ends cleanly. Reporting that as success
          // put a green 'Successfully scraped 0 pages' directly above the
          // warning saying the site had blocked us. Let the health verdict
          // decide -- it is the thing that actually knows.
          const verdict = status.ingest_health?.verdict;
          const nothingIndexed = verdict === 'blocked' || verdict === 'empty' || verdict === 'failed';
          const completedResult = {
            success: !nothingIndexed,
            hasErrors: status.status === 'completed_with_errors',
            message: verdict === 'blocked'
              ? (status.ingest_health?.access_issue === 'rate_limited'
                 ? 'This website asked us to slow down, so we couldn’t read it this time.'
                 : 'We couldn’t read this website.')
              : nothingIndexed
              ? `Scraped ${status.progress?.pages_scraped || 0} pages, but nothing usable was indexed`
              : status.status === 'completed_with_errors'
              ? `Scraped ${status.progress?.pages_scraped || 0} pages — some pages had embedding errors`
              : `Successfully scraped ${status.progress?.pages_scraped || 0} pages`,
            collection: status.collection_name || '',
            stats: {
              pages_scraped: status.progress?.pages_scraped || 0,
              chunks_created: status.progress?.chunks_created || 0,
              documents_created: status.progress?.documents_created || 0,
              pages_visited: status.progress?.pages_total || 0,
              elapsed_seconds: status.elapsed_seconds || 0,
              errors_count: status.errors_count || 0,
            },
            errors: status.errors || [],
            error_message: status.error_message || null,
            pages: status.pages || [],
            // Silent-failure guard: whether the scrape actually captured usable
            // knowledge. A job can "complete" while extracting almost nothing
            // (JS-only pages, login walls, robots). We warn instead of implying
            // the agent is ready.
            ingest_health: status.ingest_health || null,
          };
          setResult(completedResult);
          // Anything indexed goes on to the server's readiness decision; a thin
          // crawl is limited, not refused, and the preview says so.
          if (completedResult.stats.chunks_created > 0) {
            onCompleteRef.current?.({ result: completedResult, collectionId });
          } else {
            trackOnboarding('knowledge_rejected', {
              method: 'website',
              verdict: completedResult.ingest_health?.verdict || 'empty',
              chunks: completedResult.stats.chunks_created,
              pages: completedResult.ingest_health?.pages_embedded ?? 0,
              access_issue: completedResult.ingest_health?.access_issue || null,
            });
          }
          if (!embedded) {
            loadJobHistory(); // Refresh job list
          }
        } else if (status.status === 'failed') {
          trackOnboarding('knowledge_failed', { method: 'website', detail: status.error_message || 'Scraping failed' });
          stopPolling();
          setScraping(false);
          setError(status.error_message || 'Scraping failed');
          loadJobHistory(); // Refresh job list
        } else if (status.status === 'cancelled') {
          stopPolling();
          setScraping(false);
          setError('Scraping was cancelled');
        }
      } catch (err) {
        console.error('Error polling job status:', err);
        consecutiveFailures += 1;
        const statusCode = err?.response?.status;
        const permanentFailure = [401, 403, 404].includes(statusCode);
        if (permanentFailure || consecutiveFailures >= 5) {
          stopPolling();
          setScraping(false);
          const detail = err?.response?.data?.detail;
          setError(
            detail ||
              (statusCode === 404
                ? 'This scrape job no longer exists.'
                : 'Could not load this scrape job. Please return to Knowledge Sources and try again.')
          );
        }
      }
    };
    
    // Install the interval before the immediate request so an immediately
    // terminal job can reliably clear it.
    pollingRef.current = setInterval(pollJobStatus, 3000);
    pollJobStatus();
  };

  const attachToJob = (jobId, updateUrl = true) => {
    attachedJobRef.current = jobId;
    setCurrentJobId(jobId);
    setJobStatus(null);
    setResult(null);
    setError('');
    setScraping(true);
    if (updateUrl) {
      setSearchParams({ job: jobId }, { replace: true });
    }
    startPolling(jobId);
  };

  // Knowledge Sources starts a refresh and sends the operator here with the
  // returned job ID. Attach to that existing job instead of creating another.
  useEffect(() => {
    if (!attachedJobId || attachedJobRef.current === attachedJobId) return;
    attachToJob(attachedJobId, false);
  }, [attachedJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Signup already asked for the website, so it arrives filled in. Filled in,
  // not started: the customer still decides when to go, and can correct the
  // address or choose a different source first.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!initialUrl || prefilledRef.current) return;
    prefilledRef.current = true;
    setUrl(initialUrl);
  }, [initialUrl]);

  const handleScrape = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    setJobStatus(null);

    const target = normaliseUrl(url);
    if (!target) {
      setError('Please enter your website address');
      return;
    }

    if (!validateUrl(target)) {
      trackOnboarding('knowledge_form_invalid', { method: 'website', reason: 'invalid_url', typed: url });
      setError('That does not look like a website address. Example: yourbusiness.com');
      return;
    }
    if (target !== url) setUrl(target);

    if (!collectionId && !resolveCollection) {
      trackOnboarding('knowledge_form_invalid', { method: 'website', reason: 'no_collection' });
      setError('Please select a collection');
      return;
    }

    setScraping(true);
    // The scrape request carries no workspace in the server log; this does.
    trackOnboarding('scrape_started', { host: new URL(target).hostname });

    try {
      // Setup creates its collection now that there is something to put in it.
      const targetId = resolveCollection ? String((await resolveCollection()).id) : collectionId;
      setCollectionId(targetId);
      const scrapeData = {
        url: target,
        collection_id: targetId,
        max_pages: options.max_pages,
        max_depth: options.max_depth,
        include_patterns: options.include_patterns ? options.include_patterns.split(',').map(p => p.trim()).filter(p => p) : null,
        exclude_patterns: options.exclude_patterns ? options.exclude_patterns.split(',').map(p => p.trim()).filter(p => p) : null,
        use_sitemap: options.use_sitemap,
        rate_limit_delay: options.rate_limit_delay,
      };

      console.log('🌐 Starting scrape job:', scrapeData);
      const response = await ragAPI.scrapeWebsite(scrapeData);
      console.log('✅ Job started:', response.data);
      
      // New background job response has job_id
      if (response.data.job_id) {
        setCurrentJobId(response.data.job_id);
        startPolling(response.data.job_id);
      } else {
        // Fallback for old synchronous response format (backwards compatibility)
        setResult(response.data);
        setScraping(false);
        if (response.data?.success && response.data?.stats?.chunks_created > 0) {
          onCompleteRef.current?.({ result: response.data, collectionId });
        }
      }
    } catch (err) {
      console.error('❌ Scrape failed:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Scraping failed';
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      setScraping(false);
    }
  };

  const handleResume = async () => {
    if (!currentJobId) return;
    
    setError('');
    setResult(null);  // Clear previous results so polling UI shows
    setScraping(true);
    
    try {
      console.log('🔄 Resuming job:', currentJobId);
      await ragAPI.resumeScrapeJob(currentJobId);
      startPolling(currentJobId);
    } catch (err) {
      console.error('❌ Resume failed:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Resume failed';
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      setScraping(false);
    }
  };

  const handleRetryUrl = async (targetUrl) => {
    if (!currentJobId) return;
    
    setError('');
    setResult(null);
    setScraping(true);
    
    try {
      console.log('🔄 Retrying single URL:', targetUrl);
      await ragAPI.retryUrl(currentJobId, targetUrl);
      startPolling(currentJobId);
    } catch (err) {
      console.error('❌ Retry URL failed:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Retry failed';
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      setScraping(false);
    }
  };

  const handleRefreshSite = async (originJobId) => {
    if (!canManageSources || siteRefreshInFlightRef.current || scraping) return;

    siteRefreshInFlightRef.current = true;
    setRefreshingSiteJobId(originJobId);
    try {
      const response = await ragAPI.refreshScrapeJob(originJobId, { scope: 'site' });
      const refreshJobId = response.data?.job_id;
      if (!refreshJobId) {
        throw new Error('Refresh started without a job ID');
      }
      attachToJob(refreshJobId);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Site refresh failed';
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      siteRefreshInFlightRef.current = false;
      setRefreshingSiteJobId(null);
    }
  };

  const filteredCollections = useMemo(() => {
    const q = collectionSearch.trim().toLowerCase();
    if (!q) return collections;

    return collections.filter((collection) => {
      const haystack = [
        collection.name,
        collection.collection_name,
        collection.internal_name,
        collection.description,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [collections, collectionSearch]);

  const handleCancel = async () => {
    if (!currentJobId) return;
    
    try {
      console.log('⏹ Cancelling job:', currentJobId);
      const response = await ragAPI.cancelScrapeJob(currentJobId);
      setJobStatus((current) => ({
        ...(current || {}),
        status: response.data?.status || 'cancelling',
      }));
      // Cancellation is cooperative. Keep polling until the worker reports
      // terminal `cancelled`; it may still be draining an in-flight fetch or
      // embedding call after the DELETE request returns.
      setScraping(true);
    } catch (err) {
      console.error('❌ Cancel failed:', err);
    }
  };

  const handleReset = () => {
    // Stop any polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setUrl('');
    setResult(null);
    setError('');
    setCurrentJobId(null);
    setJobStatus(null);
    setScraping(false);
    attachedJobRef.current = null;
    setSearchParams({}, { replace: true });
    // Reset to config defaults if available, otherwise use hardcoded defaults
    setOptions({
      max_pages: configDefaults?.max_pages || 50,
      max_depth: configDefaults?.max_depth || 3,
      include_patterns: '',
      exclude_patterns: '',
      use_sitemap: configDefaults?.use_sitemap ?? true,
      rate_limit_delay: configDefaults?.rate_limit_delay || 0.5,
    });
  };

  const handlePastedContentSaved = (payload) => {
    const selectedCollection = collections.find(
      (collection) => String(collection.id) === String(collectionId),
    );
    onCompleteRef.current?.({ response: payload, collection: selectedCollection });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (collections.length === 0 && !resolveCollection) {
    return (
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Web Scraper</h1>
          <p className="mt-1 text-sm text-gray-600">Add pages from your website for your agent to answer from</p>
        </div>

        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-8 text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-yellow-900 mb-2">No Collections Found</h2>
          <p className="text-yellow-800 mb-6">
            You need to create a collection before scraping websites.
          </p>
          <button
            onClick={() => navigate('/rag/collections')}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Create Collection
          </button>
        </div>
      </div>
    );
  }

  // Split job.errors into actual page failures (retryable via resume, which
  // only accepts failed/cancelled/partial jobs) vs. durable-backup warnings
  // (the page is already "embedded" and the job is COMPLETED — retrying would
  // 400, and there's nothing to re-fetch since the content is already
  // searchable; this just flags that its durable copy needs attention).
  const pageErrors = (result?.errors || []).filter((err) => err.type !== 'durable_activation_failed');
  // Resume re-embeds pages we already SAVED, so it can only fix an
  // embedding failure. The server classifies each error at the point it
  // happened -- a status-based guess here misread both ways: a Playwright
  // failure, redirect loop or timeout carries no status but is a crawl
  // failure, and an HTTP 500 carries one but is not the site refusing us.
  //
  // Fails closed: an error from an older job, or a new server call site
  // that forgot to classify itself, is never offered a retry.
  const embeddingErrors = pageErrors.filter((err) => err.resume_retryable === true);
  const crawlErrors = pageErrors.filter((err) => err.phase === 'crawl');
  const backupWarnings = (result?.errors || []).filter((err) => err.type === 'durable_activation_failed');

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 space-y-6'}>
      {/* Header */}
      {!embedded && <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <GlobeAltIcon className="h-8 w-8 mr-2 text-indigo-600" />
          Web Scraper
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Scrape websites and automatically create embeddings for your RAG system
        </p>
      </div>}

      <div className={embedded ? 'space-y-6' : 'grid grid-cols-1 lg:grid-cols-2 gap-6'}>
        {/* Input Form */}
        <div className={embedded ? 'rounded-xl border-2 border-blue-300 bg-blue-50 p-5 sm:p-6' : 'bg-white rounded-lg shadow-sm border border-gray-200 p-6'}>
          <form onSubmit={handleScrape} className="space-y-5">
            {/* URL Input */}
            <div>
              {embedded && <div className="mb-2 text-xs font-bold uppercase tracking-wide text-blue-800">Enter this first</div>}
              <label className="block text-sm font-semibold text-gray-800 mb-1" htmlFor={embedded ? 'onboarding-website-url' : undefined}>
                {embedded ? 'Your business website address' : 'Website URL'}
              </label>
              <div className="relative">
                <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id={embedded ? 'onboarding-website-url' : undefined}
                  type="text"
                  inputMode="url"
                  onBlur={() => setUrl((current) => normaliseUrl(current))}
                  autoFocus={embedded}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  className={`${embedded ? 'min-h-[54px] border-2 border-blue-500 text-base shadow-sm ring-4 ring-blue-100' : 'border border-gray-300'} w-full rounded-lg py-2.5 pl-10 pr-4 caret-blue-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500`}
                  disabled={scraping}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {embedded
                  ? 'Example: https://yourbusiness.com — Serviti will find the useful pages automatically.'
                  : 'Enter the starting URL to scrape. The bot will follow links within the same domain.'}
              </p>
              {!embedded && <p className="mt-1 text-xs text-gray-600">
                <strong>Limits:</strong> up to <strong>100 URLs per crawl</strong> by default (raise below if you need more). Each page counts as 1 of the knowledge pages your plan includes. Text pages work best — image-only pages get summarized visually, which captures topics but not exact wording.
              </p>}
            </div>

            {/* Collection Selector — Explicit Checkboxes */}
            {!embedded && <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FolderIcon className="h-4 w-4 inline mr-1 text-gray-500" />
                Target Collection <span className="text-red-500">*</span>
              </label>
              {!collectionId && (
                <div className="mb-2 p-2 bg-amber-50 border border-amber-300 rounded-lg flex items-center text-xs text-amber-800">
                  <ExclamationCircleIcon className="h-4 w-4 text-amber-500 mr-1.5 flex-shrink-0" />
                  You must select a collection where scraped content will be stored
                </div>
              )}
              <div className="mb-3">
                <input
                  type="search"
                  value={collectionSearch}
                  onChange={(e) => setCollectionSearch(e.target.value)}
                  placeholder="Search collections by name, internal name, or description..."
                  disabled={scraping}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:text-gray-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Showing {filteredCollections.length} of {collections.length} collections.
                </p>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                {filteredCollections.length === 0 ? (
                  <p className="p-4 text-sm text-gray-400 text-center">No collections match your search</p>
                ) : (
                  filteredCollections.map((collection) => (
                    <label
                      key={collection.id}
                      className={`flex items-center p-2.5 rounded-lg cursor-pointer transition-colors ${
                        collectionId === String(collection.id)
                          ? 'bg-indigo-50 border border-indigo-300 ring-1 ring-indigo-400'
                          : 'bg-white border border-gray-200 hover:bg-gray-50'
                      } ${scraping ? 'opacity-60 pointer-events-none' : ''}`}
                    >
                      <input
                        type="radio"
                        name="target-collection"
                        value={collection.id}
                        checked={collectionId === String(collection.id)}
                        onChange={(e) => setCollectionId(e.target.value)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                        disabled={scraping}
                      />
                      <div className="ml-3 flex-1 min-w-0">
                        <span className="text-sm font-medium text-gray-900">{collection.name}</span>
                        <span className="text-xs text-gray-500 ml-2">({collection.documents_count || 0} docs)</span>
                      </div>
                      {collectionId === String(collection.id) && (
                        <CheckCircleIcon className="h-5 w-5 text-indigo-600 flex-shrink-0" />
                      )}
                    </label>
                  ))
                )}
              </div>
            </div>}

            {/* Advanced Options Toggle */}
            {!embedded && <div>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900"
              >
                <CogIcon className="h-4 w-4 mr-1" />
                Advanced Options
                {showAdvanced ? (
                  <ChevronUpIcon className="h-4 w-4 ml-1" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 ml-1" />
                )}
              </button>
            </div>}

            {/* Advanced Options */}
            {!embedded && showAdvanced && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Pages
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={options.max_pages}
                      onChange={(e) => setOptions(prev => ({ ...prev, max_pages: parseInt(e.target.value) || 10 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      disabled={scraping}
                    />
                    <p className="mt-1 text-xs text-gray-500">Max URLs to crawl in this run. Higher values use more of your Knowledge pages tier budget.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Depth
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={options.max_depth}
                      onChange={(e) => setOptions(prev => ({ ...prev, max_depth: parseInt(e.target.value) || 2 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      disabled={scraping}
                    />
                    <p className="mt-1 text-xs text-gray-500">Link following depth</p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <label className="block text-sm font-medium text-blue-800 mb-1">
                    Include Patterns (optional — override default exclusions)
                  </label>
                  <input
                    type="text"
                    value={options.include_patterns}
                    onChange={(e) => setOptions(prev => ({ ...prev, include_patterns: e.target.value }))}
                    placeholder="/admissions, /programs, /fees"
                    className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    disabled={scraping}
                  />
                  <p className="mt-1 text-xs text-blue-600">Comma-separated patterns. Only URLs matching these will be scraped. Use this to focus on specific sections.</p>
                </div>

                <div>
                  <p className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                    Blog, news, events, and other low-value pages are automatically deprioritized. Navigation menu pages are scraped first.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rate Limit (seconds)
                    </label>
                    <input
                      type="number"
                      min="0.1"
                      max="5"
                      step="0.1"
                      value={options.rate_limit_delay}
                      onChange={(e) => setOptions(prev => ({ ...prev, rate_limit_delay: parseFloat(e.target.value) || 0.5 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      disabled={scraping}
                    />
                    <p className="mt-1 text-xs text-gray-500">Delay between requests</p>
                  </div>
                  <div className="flex items-center pt-6">
                    <input
                      type="checkbox"
                      id="use_sitemap"
                      checked={options.use_sitemap}
                      onChange={(e) => setOptions(prev => ({ ...prev, use_sitemap: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      disabled={scraping}
                    />
                    <label htmlFor="use_sitemap" className="ml-2 text-sm text-gray-700">
                      Parse sitemap.xml
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="submit"
                disabled={scraping || !url.trim()}
                className={`${embedded ? 'min-h-[52px] text-base font-bold shadow-md' : ''} flex flex-1 items-center justify-center rounded-lg bg-indigo-600 px-4 py-2.5 text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {scraping ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                    Scraping...
                  </>
                ) : (
                  <>
                    <GlobeAltIcon className="h-5 w-5 mr-2" />
                    {embedded ? 'Read my website and continue' : 'Start Scraping'}
                  </>
                )}
              </button>
              {!embedded && <button
                type="button"
                onClick={handleReset}
                disabled={scraping}
                className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Reset
              </button>}
            </div>
          </form>
        </div>

        {/* Results Panel */}
        {(!embedded || result || scraping || jobStatus) && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <DocumentTextIcon className="h-5 w-5 mr-2 text-gray-500" />
            Scrape Results
          </h2>

          {!result && !scraping && !jobStatus && (
            <div className="text-center py-12 text-gray-500">
              <GlobeAltIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p>Enter a URL and click "Start Scraping" to begin</p>
            </div>
          )}

          {scraping && (
            <div className="py-8 space-y-4">
              <div className="text-center">
                <ArrowPathIcon className="h-12 w-12 mx-auto mb-4 text-indigo-600 animate-spin" />
                <p className="text-gray-600 font-medium">
                  {jobStatus?.status === 'scraping' ? 'Phase 1: Scraping pages...'
                    : jobStatus?.status === 'embedding' ? 'Phase 2: Embedding content...'
                    : jobStatus?.status === 'running' ? 'Scraping in progress...'
                    : jobStatus?.status === 'cancelling' ? 'Stopping safely...'
                    : 'Starting scrape job...'}
                </p>
              </div>
              
              {/* Progress Details */}
              {jobStatus && (
                <div className="space-y-3">
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${jobStatus.progress?.percent || 0}%` }}
                    ></div>
                  </div>
                  
                  {/* Progress Stats */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div className="bg-gray-50 p-3 rounded-lg text-center">
                      <p className="text-xl font-bold text-indigo-600">{jobStatus.progress?.pages_scraped || 0}</p>
                      <p className="text-xs text-gray-600">Pages Scraped</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg text-center">
                      <p className="text-xl font-bold text-indigo-600">{jobStatus.progress?.chunks_created || 0}</p>
                      <p className="text-xs text-gray-600">Chunks Created</p>
                    </div>
                  </div>
                  
                  {/* Current URL */}
                  {jobStatus.progress?.current_url && (
                    <div className="text-xs text-gray-500 truncate">
                      <span className="font-medium">Processing:</span> {jobStatus.progress.current_url}
                    </div>
                  )}
                  
                  {/* Elapsed Time */}
                  <div className="text-xs text-gray-500 text-center">
                    Elapsed: {Math.round(jobStatus.elapsed_seconds || 0)}s
                  </div>
                  
                  {/* Embedding progress indicator */}
                  {jobStatus.progress?.pages_scraped > 0 && jobStatus.progress?.chunks_created === 0 && jobStatus.elapsed_seconds > 60 && (
                    <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs text-amber-700">
                        ⏳ <strong>Embedding phase:</strong> Pages were scraped successfully. Now generating vector embeddings — this may take a few minutes depending on the embedding service.
                      </p>
                    </div>
                  )}
                  {/* Cancel Button */}
                  <div className="text-center pt-2">
                    <button
                      type="button"
                      onClick={handleCancel}
                      disabled={jobStatus?.status === 'cancelling'}
                      className="px-4 py-2 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <StopIcon className="h-4 w-4 inline mr-1" />
                      {jobStatus?.status === 'cancelling' ? 'Stopping…' : 'Cancel Scraping'}
                    </button>
                  </div>
                </div>
              )}
              
              {!jobStatus && (
                <p className="text-sm text-gray-500 text-center">Initializing job...</p>
              )}
            </div>
          )}

          {/* Failed job with resume option */}
          {!scraping && jobStatus?.status === 'failed' && !result && (
            <div className="py-6 space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <ExclamationCircleIcon className="h-6 w-6 text-red-500 inline mr-2" />
                <span className="text-red-800 font-medium">Scraping failed</span>
                <p className="text-sm text-red-700 mt-1">{jobStatus.error_message || 'An error occurred'}</p>
              </div>
              
              {/* Progress made before failure */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <p className="text-xl font-bold text-gray-600">{jobStatus.progress?.pages_scraped || 0}</p>
                  <p className="text-xs text-gray-600">Pages Completed</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <p className="text-xl font-bold text-gray-600">{jobStatus.progress?.chunks_created || 0}</p>
                  <p className="text-xs text-gray-600">Chunks Saved</p>
                </div>
              </div>
              
              {/* Resume Button */}
              <div className="flex justify-center space-x-3">
                <button
                  type="button"
                  onClick={handleResume}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <PlayIcon className="h-4 w-4 inline mr-1" />
                  Resume — Embed Saved Pages
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Start Over
                </button>
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              {/* Success / Partial Success / Error Banner */}
              <div className={`p-4 rounded-lg flex items-start ${
                result.hasErrors
                  ? 'bg-amber-50 border border-amber-200'
                  : result.success
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-red-50 border border-red-200'
              }`}>
                {result.hasErrors ? (
                  <ExclamationCircleIcon className="h-6 w-6 text-amber-500 mr-3 flex-shrink-0" />
                ) : result.success ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                ) : (
                  <ExclamationCircleIcon className="h-6 w-6 text-red-500 mr-3 flex-shrink-0" />
                )}
                <div>
                  <p className={`font-medium ${
                    result.hasErrors ? 'text-amber-800' : result.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {result.message}
                  </p>
                  {result.error_message && (
                    <p className="text-sm mt-1 text-gray-600">{result.error_message}</p>
                  )}
                  <p className="text-sm mt-1 text-gray-600">
                    Collection: <span className="font-medium">{result.collection}</span>
                  </p>
                </div>
              </div>

              {/* Silent-failure guard: a scrape can "complete" while extracting
                  almost nothing (JS-only pages, login walls, robots). Warn clearly
                  instead of letting the green banner imply the agent is ready. */}
              {result.ingest_health && !result.ingest_health.sufficient
                && result.ingest_health.verdict !== 'in_progress' && (
                <div className={`p-4 rounded-lg border flex items-start ${
                  result.ingest_health.verdict === 'thin'
                    ? 'bg-amber-50 border-amber-300'
                    : 'bg-red-50 border-red-300'
                }`}>
                  <ExclamationCircleIcon className={`h-6 w-6 mr-3 flex-shrink-0 ${
                    result.ingest_health.verdict === 'thin' ? 'text-amber-500' : 'text-red-500'
                  }`} />
                  <div>
                    <p className={`font-semibold ${
                      result.ingest_health.verdict === 'thin' ? 'text-amber-800' : 'text-red-800'
                    }`}>
                      {result.ingest_health.verdict === 'blocked'
                        ? (result.ingest_health.reasons?.[0]
                           || 'The site did not return any readable pages to our crawler.')
                        : result.ingest_health.verdict === 'empty'
                        ? 'We could not read enough text from this website automatically.'
                        : result.ingest_health.verdict === 'failed'
                          ? 'The scrape did not index this site.'
                          : 'Only a little content was indexed — your agent may not be ready to go live yet.'}
                    </p>
                    {/* Blocked uses reasons[0] as its headline above, so
                        start the list after it rather than repeating it. */}
                    {Array.isArray(result.ingest_health.reasons)
                      && result.ingest_health.reasons.slice(
                           result.ingest_health.verdict === 'blocked' ? 1 : 0
                         ).length > 0 && (
                      <ul className="text-sm mt-2 text-gray-700 list-disc list-inside space-y-1">
                        {result.ingest_health.reasons
                          .slice(result.ingest_health.verdict === 'blocked' ? 1 : 0)
                          .map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    )}
                    {result.ingest_health.recommendation
                      && result.ingest_health.verdict !== 'empty' && (
                      /* whitespace-pre-line: the blocked recommendation is
                         numbered steps. Without it they collapse into one
                         paragraph and stop reading as instructions. */
                      <p className="text-sm mt-2 font-medium text-gray-800 whitespace-pre-line">
                        {result.ingest_health.recommendation}
                      </p>
                    )}
                    <p className="text-xs mt-2 text-gray-500">
                      Indexed {result.ingest_health.indexed_chars ?? 0} characters across {result.ingest_health.pages_embedded ?? 0} page(s).
                    </p>
                  </div>
                </div>
              )}

              {result.ingest_health?.verdict === 'empty' && (
                <PasteContentRecovery
                  sourceUrl={jobStatus?.url || url}
                  collectionId={collectionId}
                  onSaved={handlePastedContentSaved}
                />
              )}

              {/* Durable-backup warnings: page is embedded and searchable, only its
                  durable copy needs attention. No retry button — the job is
                  COMPLETED (resume only accepts failed/cancelled/partial jobs) and
                  there's nothing to re-fetch, the content already indexed fine. */}
              {backupWarnings.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-amber-800 mb-2">
                    ⚠️ {backupWarnings.length} page(s) need backup attention
                  </h3>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {backupWarnings.map((err, idx) => (
                      <div key={idx} className="text-xs text-amber-700 flex items-start gap-2">
                        <span className="text-amber-400">•</span>
                        <div className="min-w-0">
                          <span className="font-medium truncate block">{err.url}</span>
                          <span className="text-amber-600">{err.error}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error details for partial success.
                  Never for a blocked crawl: recording the 403s is what makes
                  the blocked verdict possible, but they land in the same array
                  that drives this panel -- switching on a red 'Retry Failed
                  Pages' button for a job that cannot be resumed. A 403-only
                  crawl finishes 'completed', and the resume endpoint rejects
                  completed jobs with a 400. The refusals are already explained
                  above; repeating them under a dead button only misleads. */}
              {/* Refused URLs on an otherwise-successful crawl. Stated, but
                  with no retry affordance: resume cannot re-fetch them. When
                  the whole crawl was refused the blocked warning above already
                  covers it, so this would be a second telling. */}
              {crawlErrors.length > 0 && result.ingest_health?.verdict !== 'blocked' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                  {crawlErrors.length} page(s) could not be fetched and were skipped
                  {crawlErrors[0]?.status ? ` (HTTP ${crawlErrors[0].status})` : ''}.
                  {/* Not necessarily a refusal: a 500, a timeout or a redirect
                      loop lands here too. State the effect, not a cause we
                      cannot see. */}
                  Re-running the crawl may pick them up; uploading or pasting that
                  content is the certain route if the agent needs it.
                </div>
              )}

              {embeddingErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-red-800">
                      ⚠️ {embeddingErrors.length} page(s) failed to index
                    </h3>
                    <button
                      type="button"
                      onClick={handleResume}
                      className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1"
                    >
                      🔄 Retry Failed Pages
                    </button>
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {embeddingErrors.map((err, idx) => (
                      <div key={idx} className="text-xs text-red-700 flex items-start gap-2">
                        <span className="text-red-400">•</span>
                        <div className="min-w-0">
                          <span className="font-medium truncate block">{err.url}</span>
                          <span className="text-red-500">{err.error}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-red-500 mt-2 italic">
                    Retry will re-embed the failed pages from saved content — no re-scraping needed.
                  </p>
                </div>
              )}

              {/* Stats */}
              {result.stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-gray-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-indigo-600">{result.stats.pages_scraped}</p>
                    <p className="text-xs text-gray-600">Pages Scraped</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-indigo-600">{result.stats.chunks_created}</p>
                    <p className="text-xs text-gray-600">Chunks Created</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">{result.stats.unique_content || result.stats.pages_scraped}</p>
                    <p className="text-xs text-gray-600">Unique Content</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">{result.stats.elapsed_seconds || '-'}s</p>
                    <p className="text-xs text-gray-600">Duration</p>
                  </div>
                </div>
              )}

              {/* Additional Stats Row */}
              {result.stats && (result.stats.pages_visited || result.stats.pages_per_second) && (
                <div className="flex justify-between text-xs text-gray-500 px-1">
                  <span>Visited: {result.stats.pages_visited} URLs</span>
                  {result.stats.pages_per_second && <span>Speed: {result.stats.pages_per_second} pages/sec</span>}
                </div>
              )}

              {/* Scraped Pages */}
              {result.pages && result.pages.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Scraped Pages ({result.pages.length})</h3>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {result.pages.map((page, index) => (
                      <div key={index} className={`flex items-center p-2 rounded text-sm ${page.is_thin ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                        {page.is_thin ? (
                          <ExclamationCircleIcon className="h-4 w-4 text-amber-500 mr-2 flex-shrink-0" title="Very little text extracted" />
                        ) : (
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-900 truncate">{page.title}</p>
                          <p className="text-xs text-gray-500 truncate">{page.url}</p>
                        </div>
                        <div className="flex items-center space-x-2 ml-2">
                          {page.is_thin && (
                            <span className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded font-medium">thin</span>
                          )}
                          {page.depth !== undefined && (
                            <span className="text-xs text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded">d{page.depth}</span>
                          )}
                          <span className="text-xs text-gray-400">{Math.round((page.content_length || 0) / 1000)}k</span>
                          <button
                            onClick={() => viewPageText(page)}
                            className="text-xs text-indigo-600 hover:text-indigo-800 underline flex-shrink-0"
                            title="View the text this page extracted"
                          >
                            View text
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Errors */}
              {pageErrors.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-red-700 mb-2">Errors ({pageErrors.length})</h3>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {pageErrors.map((err, index) => (
                      <div key={index} className="flex items-start p-2 bg-red-50 rounded text-sm">
                        <ExclamationCircleIcon className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-xs text-red-700 truncate">{err.url}</p>
                          <p className="text-xs text-red-600">{err.error}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Durable-backup warnings (see the panel above for the primary view;
                  this mirrors it for this secondary results view). */}
              {backupWarnings.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-amber-700 mb-2">Backup attention needed ({backupWarnings.length})</h3>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {backupWarnings.map((err, index) => (
                      <div key={index} className="flex items-start p-2 bg-amber-50 rounded text-sm">
                        <ExclamationCircleIcon className="h-4 w-4 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-xs text-amber-700 truncate">{err.url}</p>
                          <p className="text-xs text-amber-600">{err.error}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>}
      </div>

      {/* ======== URL STATUS TABLE ======== */}
      {(() => {
        // Show pages from either live jobStatus or completed result
        const pageList = jobStatus?.pages || result?.pages || [];
        if (pageList.length === 0) return null;

        const statusOrder = { embedding: 0, pending: 1, failed: 2, skipped: 3, embedded: 4 };
        const sorted = [...pageList].sort((a, b) => (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9));

        const counts = pageList.reduce((acc, d) => {
          acc[d.status] = (acc[d.status] || 0) + 1;
          return acc;
        }, {});

        const badgeClass = (status) => {
          switch (status) {
            case 'embedded': return 'bg-green-100 text-green-800';
            case 'embedding': return 'bg-blue-100 text-blue-800 animate-pulse';
            case 'pending': return 'bg-gray-100 text-gray-700';
            case 'failed': return 'bg-red-100 text-red-800';
            case 'skipped': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-600';
          }
        };

        const isRetryable = !scraping && (
          jobStatus?.status === 'completed_with_errors' ||
          jobStatus?.status === 'failed' ||
          jobStatus?.status === 'completed' ||
          result?.hasErrors
        );

        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Header + summary badges */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <LinkIcon className="h-5 w-5 mr-2 text-gray-500" />
                URL Status ({pageList.length} pages)
              </h2>
              <div className="flex gap-2 flex-wrap">
                {counts.embedded > 0 && (
                  <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 font-medium">
                    ✓ {counts.embedded} embedded
                  </span>
                )}
                {counts.pending > 0 && (
                  <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 font-medium">
                    ◻ {counts.pending} pending
                  </span>
                )}
                {counts.embedding > 0 && (
                  <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800 font-medium animate-pulse">
                    ⟳ {counts.embedding} embedding
                  </span>
                )}
                {counts.failed > 0 && (
                  <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800 font-medium">
                    ✗ {counts.failed} failed
                  </span>
                )}
                {counts.skipped > 0 && (
                  <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 font-medium">
                    — {counts.skipped} skipped
                  </span>
                )}
              </div>
            </div>

            {/* Retry All Failed button */}
            {isRetryable && counts.failed > 0 && (
              <div className="mb-3">
                <button
                  type="button"
                  onClick={handleResume}
                  className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  🔄 Retry All {counts.failed} Failed
                </button>
              </div>
            )}

            {/* URL Table */}
            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">#</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">URL</th>
                    <th className="px-4 py-2 text-center font-medium text-gray-600">Status</th>
                    <th className="px-4 py-2 text-center font-medium text-gray-600">Chunks</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Error</th>
                    <th className="px-4 py-2 text-center font-medium text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sorted.map((detail, idx) => (
                    <tr key={idx} className={detail.status === 'embedding' ? 'bg-blue-50/40' : ''}>
                      <td className="px-4 py-2 text-gray-400 text-xs">{idx + 1}</td>
                      <td className="px-4 py-2 max-w-xs">
                        <a
                          href={detail.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:underline truncate block text-xs"
                          title={detail.url}
                        >
                          {detail.url}
                        </a>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass(detail.status)}`}>
                          {detail.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center text-xs text-gray-600">
                        {detail.chunk_count != null ? detail.chunk_count : '—'}
                      </td>
                      <td className="px-4 py-2 text-xs text-red-600 max-w-xs truncate" title={detail.error_message || ''}>
                        {detail.error_message || ''}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {detail.status === 'failed' && isRetryable && (
                          <button
                            type="button"
                            onClick={() => handleRetryUrl(detail.url)}
                            className="px-2 py-1 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                            title={`Retry embedding for ${detail.url}`}
                          >
                            ↻ Retry
                          </button>
                        )}
                        {detail.status === 'embedding' && (
                          <ArrowPathIcon className="h-4 w-4 text-blue-500 animate-spin inline" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ======== JOB HISTORY PANEL ======== */}
      {!embedded && pastJobs.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Collapsible header */}
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
          >
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <ClockIcon className="h-5 w-5 mr-2 text-gray-500" />
              Scrape Jobs ({pastJobs.length})
            </h2>
            {showHistory ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {showHistory && (
            <div className="px-6 pb-6 space-y-2">
              {pastJobs.map(job => {
                const isActive = ['running', 'scraping', 'embedding', 'pending', 'cancelling'].includes(job.status);
                const isFailed = ['failed', 'cancelled', 'completed_with_errors'].includes(job.status);
                const isExpanded = selectedJob === job.id;
                const canRefreshWholeSite =
                  canManageSources && isExpanded && job.status === 'completed' && !job.refresh_of_job_id;

                const statusBadge = (s) => {
                  switch (s) {
                    case 'completed': return 'bg-green-100 text-green-800';
                    case 'completed_with_errors': return 'bg-amber-100 text-amber-800';
                    case 'failed': return 'bg-red-100 text-red-800';
                    case 'cancelled': return 'bg-gray-100 text-gray-600';
                    case 'scraping': return 'bg-blue-100 text-blue-800 animate-pulse';
                    case 'embedding': return 'bg-purple-100 text-purple-800 animate-pulse';
                    case 'running': return 'bg-blue-100 text-blue-800 animate-pulse';
                    case 'pending': return 'bg-yellow-100 text-yellow-800';
                    case 'cancelling': return 'bg-orange-100 text-orange-800 animate-pulse';
                    default: return 'bg-gray-100 text-gray-600';
                  }
                };

                return (
                  <div key={job.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Job Row */}
                    <div
                      className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 ${isExpanded ? 'bg-gray-50 border-b border-gray-200' : ''}`}
                      onClick={() => loadJobPages(job.id)}
                    >
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusBadge(job.status)}`}>
                          {job.status}
                        </span>
                        <span className="text-sm text-gray-900 truncate font-medium" title={job.url}>
                          {job.url}
                        </span>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {job.pages_scraped || 0} pages • {job.chunks_created || 0} chunks
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {job.created_at ? new Date(job.created_at).toLocaleDateString() : ''}
                        </span>
                        {(isFailed || job.status === 'completed_with_errors') && !isActive && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleResumeJob(job.id); }}
                            className="px-2.5 py-1 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
                          >
                            ▶ Resume All
                          </button>
                        )}
                        {isExpanded ? (
                          <ChevronUpIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        )}
                      </div>
                    </div>

                    {/* Expanded Page Details */}
                    {isExpanded && (
                      <div className="px-4 py-3">
                        {canRefreshWholeSite && (
                          <div className="flex items-center justify-between gap-3 mb-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2">
                            <p className="text-xs text-indigo-800">
                              Re-fetch this site using the saved URL, depth, sitemap and include/exclude settings.
                            </p>
                            <button
                              type="button"
                              onClick={() => handleRefreshSite(job.id)}
                              disabled={scraping || Boolean(refreshingSiteJobId)}
                              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <ArrowPathIcon
                                className={`h-3.5 w-3.5 ${refreshingSiteJobId === job.id ? 'animate-spin' : ''}`}
                              />
                              {refreshingSiteJobId === job.id ? 'Starting…' : 'Refresh whole site'}
                            </button>
                          </div>
                        )}
                        {loadingPages ? (
                          <div className="flex items-center justify-center py-4">
                            <ArrowPathIcon className="h-5 w-5 text-indigo-500 animate-spin mr-2" />
                            <span className="text-sm text-gray-500">Loading pages...</span>
                          </div>
                        ) : selectedJobPages.length === 0 ? (
                          <p className="text-sm text-gray-500 py-3">No pages saved yet. The scrape may not have started or completed Phase 1.</p>
                        ) : (
                          <>
                            {/* Page summary */}
                            {(() => {
                              const pgCounts = selectedJobPages.reduce((acc, p) => {
                                acc[p.status] = (acc[p.status] || 0) + 1;
                                return acc;
                              }, {});
                              return (
                                <div className="flex gap-2 flex-wrap mb-3">
                                  {pgCounts.embedded > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-medium">✓ {pgCounts.embedded} embedded</span>}
                                  {pgCounts.pending > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">◻ {pgCounts.pending} pending</span>}
                                  {pgCounts.failed > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 font-medium">✗ {pgCounts.failed} failed</span>}
                                  {pgCounts.skipped > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 font-medium">— {pgCounts.skipped} skipped</span>}
                                  {pgCounts.embedding > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-medium animate-pulse">⟳ {pgCounts.embedding} embedding</span>}
                                  {pgCounts.scraping > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800 font-medium animate-pulse">⟳ {pgCounts.scraping} scraping</span>}
                                </div>
                              );
                            })()}

                            {/* Pages table */}
                            <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                              <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50 sticky top-0">
                                  <tr>
                                    <th className="px-3 py-2 text-left font-medium text-gray-600">URL</th>
                                    <th className="px-3 py-2 text-center font-medium text-gray-600 w-24">Status</th>
                                    <th className="px-3 py-2 text-center font-medium text-gray-600 w-20">Chunks</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-600 w-48">Error</th>
                                    <th className="px-3 py-2 text-center font-medium text-gray-600 w-52">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {selectedJobPages.map((page) => {
                                    const pgBadge = (s) => {
                                      switch (s) {
                                        case 'embedded': return 'bg-green-100 text-green-800';
                                        case 'embedding': return 'bg-blue-100 text-blue-800 animate-pulse';
                                        case 'scraping': return 'bg-cyan-100 text-cyan-800 animate-pulse';
                                        case 'pending': return 'bg-gray-100 text-gray-700';
                                        case 'failed': return 'bg-red-100 text-red-800';
                                        case 'skipped': return 'bg-yellow-100 text-yellow-800';
                                        default: return 'bg-gray-100 text-gray-600';
                                      }
                                    };
                                    const isPageRetryable = ['failed', 'skipped', 'pending'].includes(page.status);
                                    const isRetrying = retryingUrl === page.url;

                                    return (
                                      <tr key={page.id} className={page.status === 'embedding' || page.status === 'scraping' ? 'bg-blue-50/40' : ''}>
                                        <td className="px-3 py-2 max-w-xs">
                                          <a href={page.url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline truncate block text-xs" title={page.url}>
                                            {page.title || page.url}
                                          </a>
                                          {page.title && <p className="text-xs text-gray-400 truncate">{page.url}</p>}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${pgBadge(page.status)}`}>
                                            {page.status}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2 text-center text-xs text-gray-600">
                                          {page.chunk_count || '—'}
                                        </td>
                                        <td className="px-3 py-2 text-xs text-red-600 max-w-xs truncate" title={page.error_message || ''}>
                                          {page.error_message || ''}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                          {isRetrying ? (
                                            <ArrowPathIcon className="h-4 w-4 text-indigo-500 animate-spin inline" />
                                          ) : isPageRetryable ? (
                                            <div className="flex items-center justify-center gap-1">
                                              <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); handlePageRetry(job.id, page.url, false); }}
                                                className="px-2 py-0.5 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                                                title="Re-embed from saved content"
                                              >
                                                ↻ Embed
                                              </button>
                                              <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); handlePageRetry(job.id, page.url, true); }}
                                                className="px-2 py-0.5 text-xs font-medium bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                                                title="Re-scrape URL from web, then embed"
                                              >
                                                🌐 Scrape
                                              </button>
                                            </div>
                                          ) : page.status === 'embedding' || page.status === 'scraping' ? (
                                            <ArrowPathIcon className="h-4 w-4 text-blue-500 animate-spin inline" />
                                          ) : null}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Refresh button */}
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={loadJobHistory}
                  className="text-xs text-indigo-600 hover:text-indigo-800 transition-colors"
                >
                  ↻ Refresh job list
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info Box */}
      {!embedded && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">How it works</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• The scraper starts at the provided URL and follows links within the same domain</li>
          <li>• It extracts text content from pages (excluding navigation, scripts, and styles)</li>
          <li>• Content is split into chunks and converted to embeddings using your configured model</li>
          <li>• Embeddings are stored in the selected collection for RAG retrieval</li>
        </ul>
      </div>}

      {/* Extracted-text viewer — inspect exactly what a page captured, so a thin
          silent-failure page is visible and fixable, not hidden. */}
      {viewingPage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setViewingPage(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-4 border-b">
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 truncate">{viewingPage.title || 'Extracted text'}</p>
                <p className="text-xs text-gray-500 truncate">{viewingPage.url}</p>
              </div>
              <button onClick={() => setViewingPage(null)} className="text-gray-400 hover:text-gray-600 ml-3">
                <span className="text-2xl leading-none">&times;</span>
              </button>
            </div>
            <div className="p-4 overflow-y-auto">
              {viewingPage.loading ? (
                <p className="text-sm text-gray-500">Loading extracted text…</p>
              ) : viewingPage.error ? (
                <p className="text-sm text-red-600">{viewingPage.error}</p>
              ) : viewingPage.content ? (
                <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words font-sans">{viewingPage.content}</pre>
              ) : (
                <p className="text-sm text-amber-700">
                  No text was extracted from this page. It may be a JavaScript-only page, blocked by a login wall or robots rules —
                  add the content manually (upload a document or paste it) so your agent can answer from it.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebScraper;
